import React, { FC, ReactNode } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { isEmoji } from '#components/utils/is-emoji.ts';
import { IconEmoji } from '#components/icon/icon-emoji.tsx';
import { useResponsiveProp } from '../../hooks/use-responsive-prop';
import type { GDSCustomizableComponent, GDSResponsiveProp } from '../../types';
import { type GDSIconToken } from '../../theme/icons/get-theme-icons';
import type { GDSColor, GDSSize } from '../../theme';
import { styled } from '../../theme';
import { isReactElement } from '../forms/utilities';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';
import { directionalityAwareAliases } from './directionality-aware-aliases';
import { useGDSIcon } from './use-gds-icon.hook';
import { useThemeOptions } from '../../hooks/use-theme';

export { useGDSIcon } from './use-gds-icon.hook';

type DirectionalityAwareAlias = keyof typeof directionalityAwareAliases;

// The syntax to retain autocomplete of GDSIconToken but also allow for path strings
// This is necessary because doing icon: GDSIconToken | string; removes autocomplete
// https://github.com/microsoft/TypeScript/issues/29729#issuecomment-460346421
type FilePath = string & { what?: any };

export type GDSIconSource = GDSIconToken | DirectionalityAwareAlias | FilePath;
export interface GDSIconProps extends GDSCustomizableComponent {
  icon: GDSIconSource | ReactNode;
  tint?: GDSColor;
  size?: GDSResponsiveProp<number | string | GDSSize>;
  label?: string;
  id?: string;
}

const DISPLAY_NAME = 'Icon';

const IconBase = styled('div', { display: 'inline-block' });

/**
 * Determines whether the icon value provided is one of the keys in the list
 * of "directionality aware" aliases.
 */
export const isDirectionalityAwareAlias = (
  icon: unknown,
): icon is DirectionalityAwareAlias =>
  typeof icon === 'string' && icon in directionalityAwareAliases;

export const extractSvgFromDataUri = (dataUri: string): string => {
  const DATA_SVG_PREFIX = 'data:image/svg+xml,';
  const encodedSvg = dataUri.substring(DATA_SVG_PREFIX.length);
  const decodedSvg = decodeURIComponent(encodedSvg);
  return decodedSvg.match(/<svg[^>]*>[\s\S]*?<\/svg>/i)?.[0] || decodedSvg;
};

export const Icon: FC<GDSIconProps> = ({
  icon: originalIcon,
  tint,
  size = '$oneAndHalf',
  label,
  css,
  className,
  'data-testid': testId,
  ...props
}) => {
  const iconSize = useResponsiveProp(size);
  const iconClassName = ['GDS-icon', className].join(' ');
  const { isGDSIconToken, getGDSIconPath } = useGDSIcon();

  const themeOptions = useThemeOptions();

  let isDirectionalityAware = false;
  let icon = originalIcon;

  if (isDirectionalityAwareAlias(icon)) {
    isDirectionalityAware = true;
    icon = directionalityAwareAliases[icon];
  }

  // If the icon is a GDSIconToken, we need to check if it's path is a data URI
  // and if so, we need to extract the SVG content from it.
  // This is a workaround for the fact that a custom icon configured using the
  // theme API can be passed as a data URI.
  if (
    isGDSIconToken(icon) &&
    getGDSIconPath(icon).startsWith('data:image/svg+xml,')
  ) {
    const svgContent = extractSvgFromDataUri(getGDSIconPath(icon));
    const sanitizedHtml = DOMPurify.sanitize(svgContent, { FORCE_BODY: true });
    const svgElement = parse(sanitizedHtml) as JSX.Element;

    return (
      <GenesisCoreInspector displayName="Icon">
        <IconBase
          as="div"
          className={iconClassName}
          aria-hidden={!label}
          aria-label={label}
          {...(label && { role: 'img' })}
          css={{
            color: tint,
            width: iconSize,
            height: iconSize,
            transform: isDirectionalityAware
              ? 'rotateY(calc(180deg * var(--league-is-rtl)))'
              : undefined,
            '@media screen and (-ms-high-contrast: active)': {
              color: 'WindowText',
            },
            '@media (forced-colors: active)': {
              color: 'CanvasText',
            },
            ...css,
          }}
          data-testid={testId || 'decoded-svg-icon'}
          {...props}
        >
          {svgElement}
        </IconBase>
      </GenesisCoreInspector>
    );
  }

  if (isGDSIconToken(icon)) {
    const iconPath = getGDSIconPath(icon);

    const baseCSS = {
      color: tint,
      width: iconSize,
      height: iconSize,
      '&:dir(rtl)': {
        transform: isDirectionalityAware ? 'rotateY(calc(180deg))' : undefined,
      },
      '@media screen and (-ms-high-contrast: active)': {
        color: 'WindowText',
      },
      '@media (forced-colors: active)': {
        color: 'CanvasText',
      },
      ...css,
    };

    const shouldUseCSS = themeOptions?.useCSSBasedIcons;

    if (shouldUseCSS) {
      Object.assign(baseCSS, {
        backgroundColor: tint ?? 'currentColor',
        WebkitMaskImage: `url(${iconPath})`,
        maskImage: `url(${iconPath})`,
        WebkitMaskRepeat: 'no-repeat',
        maskRepeat: 'no-repeat',
        WebkitMaskSize: '100% 100%',
        maskSize: '100% 100%',
        '@media screen and (-ms-high-contrast: active)': {
          color: 'WindowText',
          backgroundColor: 'WindowText',
        },
        '@media (forced-colors: active)': {
          color: 'CanvasText',
          backgroundColor: 'CanvasText',
        },
      });
    }

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <IconBase
          as={shouldUseCSS ? 'span' : 'svg'}
          className={iconClassName}
          aria-hidden={!label}
          aria-label={label}
          css={baseCSS}
          {...(label && { role: 'img' })}
          data-testid={testId || 'svg-icon'}
          {...props}
        >
          {shouldUseCSS ? null : <use href={getGDSIconPath(icon)} />}
        </IconBase>
      </GenesisCoreInspector>
    );
  }

  if (isEmoji(icon)) {
    return (
      <IconBase
        as={IconEmoji}
        className={iconClassName}
        label={label}
        css={{ fontSize: `$sizes${iconSize}`, ...css }}
        data-testid={testId || 'emoji-icon'}
        {...props}
      >
        {icon}
      </IconBase>
    );
  }

  if (isReactElement(icon)) {
    return (
      <GenesisCoreInspector displayName="Icon">
        <IconBase
          as="div"
          className={iconClassName}
          aria-hidden={!label}
          aria-label={label}
          css={{ color: tint, width: iconSize, height: iconSize, ...css }}
          data-testid={testId || 'custom-svg-icon'}
          {...props}
        >
          {icon}
        </IconBase>
      </GenesisCoreInspector>
    );
  }

  return (
    <GenesisCoreInspector displayName="Icon">
      <IconBase
        as="img"
        className={iconClassName}
        src={icon as FilePath}
        alt={label || ''}
        css={{ width: iconSize, height: iconSize, ...css }}
        data-testid={testId || 'img-icon'}
        {...props}
      />
    </GenesisCoreInspector>
  );
};
Icon.displayName = 'Icon';
